import { useEffect, useRef, useState } from "react";
import { api, errMsg, money } from "@/lib/api";
import { BureauChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, Sparkles, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = [
  ["collection", "Collection"], ["late_payment", "Late Payment"], ["charge_off", "Charge-Off"],
  ["inquiry", "Hard Inquiry"], ["repossession", "Repossession"], ["bankruptcy", "Bankruptcy"],
  ["public_record", "Public Record"], ["other", "Other"],
];

export default function DiyItems() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bureau: "experian", creditor: "", type: "collection", account_number: "", balance: 0, date_reported: "", reason: "" });
  const [bureau, setBureau] = useState("experian");
  const [uploading, setUploading] = useState(false);
  const [parsingId, setParsingId] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/diy/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading your items…</div>;

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/diy/items", { ...form, balance: Number(form.balance) || 0 });
      toast.success("Item added");
      setOpen(false);
      setForm({ bureau: "experian", creditor: "", type: "collection", account_number: "", balance: 0, date_reported: "", reason: "" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const remove = async (id) => {
    await api.delete(`/diy/items/${id}`);
    toast.success("Item removed");
    load();
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a PDF first");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("doc_type", "credit_report");
    fd.append("bureau", bureau);
    setUploading(true);
    try {
      await api.post("/diy/documents", fd);
      toast.success("Report uploaded");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const parse = async (docId) => {
    setParsingId(docId);
    try {
      const r = await api.post(`/diy/documents/${docId}/parse`);
      toast.success(`AI found ${r.data.items_created} negative item${r.data.items_created === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setParsingId(""); }
  };

  return (
    <div className="space-y-6" data-testid="diy-items-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">My negative items</h1>
          <p className="text-slate-600 mt-1">Add the items hurting your score — or upload a credit report and let AI find them for you.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="diy-add-item-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white"><Plus className="h-4 w-4 mr-2" /> Add item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add a negative item</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bureau</Label>
                  <Select value={form.bureau} onValueChange={(v) => setForm({ ...form, bureau: v })}>
                    <SelectTrigger className="mt-1" data-testid="diy-item-bureau-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="experian">Experian</SelectItem><SelectItem value="transunion">TransUnion</SelectItem><SelectItem value="equifax">Equifax</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="diy-item-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ITEM_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Creditor / collector name</Label><Input required value={form.creditor} onChange={(e) => setForm({ ...form, creditor: e.target.value })} data-testid="diy-item-creditor-input" className="mt-1" placeholder="e.g. Midland Credit Management" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Account # (optional)</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="diy-item-account-input" className="mt-1" /></div>
                <div><Label>Balance ($)</Label><Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="diy-item-balance-input" className="mt-1" /></div>
              </div>
              <div><Label>Why is it wrong? (optional)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="diy-item-reason-input" className="mt-1" placeholder="e.g. Not my account / paid on time / too old to report" /></div>
              <Button type="submit" data-testid="diy-item-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Add item</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-5 space-y-3" data-testid="report-upload-dropzone">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> AI report scan</div>
        <p className="text-sm text-slate-600">Upload a credit report PDF and our AI will pull out every negative item automatically.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={bureau} onValueChange={setBureau}>
            <SelectTrigger className="w-40" data-testid="diy-upload-bureau-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="experian">Experian</SelectItem><SelectItem value="transunion">TransUnion</SelectItem><SelectItem value="equifax">Equifax</SelectItem></SelectContent>
          </Select>
          <Input type="file" ref={fileRef} accept="application/pdf" className="max-w-xs" data-testid="diy-report-file-input" />
          <Button onClick={upload} disabled={uploading} variant="outline" data-testid="diy-upload-btn">
            <UploadCloud className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload report"}
          </Button>
        </div>
        {data.documents.filter((d) => d.type === "credit_report").map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-slate-400" /> {d.filename} <span className="text-xs text-slate-400">· {d.bureau}</span></div>
            <Button size="sm" variant="outline" disabled={parsingId === d.id} onClick={() => parse(d.id)} data-testid={`diy-parse-${d.id.slice(0, 8)}`}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5 text-amber-500" /> {parsingId === d.id ? "Analyzing…" : d.parsed ? "Re-analyze" : "Find negative items"}
            </Button>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            {["Creditor", "Bureau", "Type", "Balance", "Why it's wrong", ""].map((h) => (
              <th key={h} className="text-left py-2.5 px-4 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.id} className="border-b border-slate-100" data-testid={`diy-item-row-${i.id.slice(0, 8)}`}>
                <td className="py-2.5 px-4 font-medium text-slate-800">{i.creditor}<div className="text-xs text-slate-400 font-normal">{i.account_number}</div></td>
                <td className="py-2.5 px-4"><BureauChip bureau={i.bureau} /></td>
                <td className="py-2.5 px-4 text-slate-600 capitalize">{i.type.replace("_", " ")}</td>
                <td className="py-2.5 px-4 text-slate-600">{i.balance ? money(i.balance) : "—"}</td>
                <td className="py-2.5 px-4 text-xs text-slate-500 max-w-56">{i.reason || "—"}</td>
                <td className="py-2.5 px-4">
                  <button onClick={() => remove(i.id)} className="text-slate-300 hover:text-red-600 transition-colors duration-150" data-testid={`diy-item-delete-${i.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No items yet — add them manually or scan a report with AI.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
